import type { ReferrerSummary } from "@redotech/redo-referrals-api/schema/shared/referrer";
import { ClientError } from "@redotech/rpc/errors";
import { useQuery } from "@tanstack/react-query";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";

import { useRedoReferralsRpcClient } from "../rpc/client-context";
import { useAuthToken } from "./auth-token-context";

type AuthStatus = "loading" | "authenticated" | "unauthenticated";

interface AuthDataContextValue {
  status: AuthStatus;
  referrer: ReferrerSummary | undefined;
}

export const AuthDataContext = createContext<AuthDataContextValue | undefined>(
  undefined,
);
AuthDataContext.displayName = "AuthDataContext";

// Routes where we don't want `getMe` to fire even if a token is present:
// the verify and impersonate pages are mid-handshake (token isn't usable
// yet) and /login is the post-logout landing surface (we deliberately
// want no referrer data here).
const PUBLIC_PATHS = new Set<string>([
  "/login",
  "/auth/verify",
  "/impersonate",
]);

export function AuthDataProvider({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const { token, clear } = useAuthToken();
  const rpcClient = useRedoReferralsRpcClient();
  const location = useLocation();
  const onPublicPath = PUBLIC_PATHS.has(location.pathname);

  const enabled = !!token && !onPublicPath;

  const query = useQuery({
    queryKey: ["me", token],
    queryFn: async () => {
      const result = await rpcClient.getMe({});
      return result.referrer;
    },
    enabled,
    // 401/403 responses mean the token is dead — retrying won't help.
    // 5xx and network failures (which aren't ClientErrors) might recover.
    retry: (failureCount, error) => {
      if (failureCount >= 2) {
        return false;
      }
      if (!(error instanceof ClientError)) {
        return true;
      }
      return error.statusCode >= 500;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Mid-session 401/403: token was valid at startup, server says no.
  // Clear the token so AuthGuard transitions us to /login and the user
  // gets a clean re-auth flow rather than a permanently-loading
  // dashboard.
  useEffect(() => {
    if (!(query.error instanceof ClientError)) {
      return;
    }
    const code = query.error.statusCode;
    if (code === 401 || code === 403) {
      clear();
    }
  }, [query.error, clear]);

  // Distinguish auth failures from transient errors. A 401/403 means
  // the session is dead → "unauthenticated" so AuthGuard redirects.
  // A 5xx or network blip leaves us in "loading" — the retry policy
  // above may still recover, and forcing a redirect-to-/login on a
  // server hiccup would silently log the user out for an issue that
  // had nothing to do with their session.
  const status: AuthStatus = useMemo(() => {
    if (!token) {
      return "unauthenticated";
    }
    if (onPublicPath) {
      return "unauthenticated";
    }
    if (query.data) {
      return "authenticated";
    }
    if (query.isError) {
      const code =
        query.error instanceof ClientError ? query.error.statusCode : undefined;
      if (code === 401 || code === 403) {
        return "unauthenticated";
      }
      return "loading";
    }
    return "loading";
  }, [token, onPublicPath, query.data, query.isError, query.error]);

  const value = useMemo<AuthDataContextValue>(
    () => ({ status, referrer: query.data }),
    [status, query.data],
  );

  return (
    <AuthDataContext.Provider value={value}>
      {children}
    </AuthDataContext.Provider>
  );
}

export function useAuthData(): AuthDataContextValue {
  const ctx = useContext(AuthDataContext);
  if (!ctx) {
    throw new Error("useAuthData must be used within AuthDataProvider");
  }
  return ctx;
}
